import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Admin } from "../../types/users";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetAdminByIdQuery,
  useAddAdminMutation,
  useEditAdminMutation,
} from "../../services/admin.service";
import { BaseModal, StyledTextField, Button } from "../../components/global";

interface ModalAdminFormProps {
  open: boolean;
  onClose: () => void;
  data: Admin | null;
}

const ModalAdminForm = ({ open, onClose, data }: ModalAdminFormProps) => {
  const { data: admin } = useGetAdminByIdQuery(data?.id!, {
    skip: !data?.id,
  });

  const [add, { isLoading: adding }] = useAddAdminMutation();
  const [edit, { isLoading: editing }] = useEditAdminMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) await add(payload).unwrap();
      else {
        if (!payload.password) delete payload.password;
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("first_name", data?.first_name || "");
      setValue("last_name", data?.last_name || "");
      setValue("phone_number", data?.phone_number || "");
      setValue("email", data?.email || "");
      setValue("password", "");
      clearErrors();
    }
  }, [data, open, setValue, clearErrors]);

  React.useEffect(() => {
    if (admin) {
      setValue("first_name", admin?.first_name || "");
      setValue("last_name", admin?.last_name || "");
      setValue("phone_number", admin?.phone_number || "");
      setValue("email", admin?.email || "");
      setValue("password", "");
    }
  }, [admin, setValue]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} Admin User`}
      maxWidth="xs"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="First Name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.first_name}
                  helperText={!!errors.first_name && "This field is required"}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Last Name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.last_name}
                  helperText={!!errors.last_name && "This field is required"}
                />
              )}
            />
            <Controller
              name="phone_number"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Phone Number"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  placeholder="Enter phone number (optional)"
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Email"
                  value={value || ""}
                  fullWidth
                  type="email"
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.email}
                  helperText={!!errors.email && "This field is required"}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: !!data ? false : true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Password"
                  value={value || ""}
                  fullWidth
                  type="password"
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.password}
                  helperText={
                    !data
                      ? (!!errors.password && "This field is required")
                      : "Leave blank if you don't want to change the password"
                  }
                />
              )}
            />
          </Stack>
        </Box>
      </form>
    </BaseModal>
  );
};
export default ModalAdminForm;
